import type { FC, ReactNode } from "react"
import { useMemo, useState } from "react"
import { TinyColor } from "@ctrl/tinycolor"
import {
	IconCheckOutline18 as Check,
	IconTagOutline18 as Tag,
} from "nucleo-ui-outline-18"
import { useToggle } from "react-use"

import type { ButtonProps } from "@productlane/ui"
import { cn } from "@productlane/lib/src/cn"
import {
	Button,
	IconButton,
	LabelsGroupIcon,
	Popover,
	Tooltip,
} from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type { RouterOutputs } from "@/lib/api"
import { useRole } from "@/lib/useRole"
import { CommandSubmenu } from "./command-submenu"
import { propertyIconClassName, PropertyTrigger } from "./property-trigger"

type Label = RouterOutputs["linear"]["labels"][number]

interface Props {
	labels: Label[]
	selectedLabelIds: string[]
	onChange: (labelId: string) => void
	variant?: ButtonProps["variant"]
	teamId: string
	onOpenChange?: (open: boolean) => void
	open?: boolean
	tooltip?: ReactNode
	// Renders the trigger as a chip in a detail page's Properties row.
	asProperty?: boolean
	emptyLabel?: string
	emptyIcon?: typeof Tag
	trigger?: ReactNode
}

export function labelsForTeam(labels: Label[], teamId: string) {
	return labels.filter(
		(label) =>
			!label.isGroup && (label.teamId === teamId || label.teamId === null),
	)
}

interface GroupedLabels {
	standalone: Label[]
	groups: Map<
		string,
		{
			groupName: string
			groupColor: string
			labels: Label[]
		}
	>
}

const LabelBadge: FC<{ label: Label; isSelected: boolean }> = ({
	label,
	isSelected,
}) => (
	<div className="flex w-full items-center gap-2">
		<div
			className="ml-1 h-[9px] w-[9px] flex-none rounded-full"
			style={{
				backgroundColor: new TinyColor(label.color).setAlpha(0.8).toString(),
			}}
		/>
		<span className="flex-1 truncate">{label.name}</span>
		{isSelected && <Check className="h-4 w-4 flex-none" />}
	</div>
)

interface CommandSubGroupProps {
	groupId: string
	groupName: string
	groupColor: string
	labels: Label[]
	selectedLabelIds: string[]
	onChange: (labelId: string) => void
	isOpen: boolean
	onOpenChange: (open: boolean) => void
}

const CommandSubGroup: FC<CommandSubGroupProps> = ({
	groupId,
	groupName,
	groupColor,
	labels,
	selectedLabelIds,
	onChange,
	isOpen,
	onOpenChange,
}) => (
	<CommandSubmenu
		value={`group-${groupId}`}
		isOpen={isOpen}
		onOpenChange={onOpenChange}
		className="pl-3"
		trigger={
			<>
				<LabelsGroupIcon
					className="h-4 w-4 flex-none rounded-full"
					style={{
						fill: new TinyColor(groupColor).setAlpha(0.8).toString(),
					}}
				/>
				<span className="flex-1 truncate">{groupName}</span>
			</>
		}
	>
		{labels.map((groupLabel) => (
			<CommandItem
				key={groupLabel.id}
				value={`${groupName}-${groupLabel.name}-${groupLabel.id}`}
				onSelect={() => {
					onChange(groupLabel.id)
					onOpenChange(false)
				}}
			>
				<LabelBadge
					label={groupLabel}
					isSelected={selectedLabelIds.includes(groupLabel.id)}
				/>
			</CommandItem>
		))}
	</CommandSubmenu>
)

const LabelMenuContent: FC<{
	groupedLabels: GroupedLabels
	selectedLabelIds: string[]
	onChange: (labelId: string) => void
	searchQuery: string
	onSearchChange: (query: string) => void
}> = ({
	groupedLabels,
	selectedLabelIds,
	onChange,
	searchQuery,
	onSearchChange,
}) => {
	const allLabelsForSearch = useMemo(() => {
		const labels: Array<{ label: Label; groupName?: string }> = []

		groupedLabels.standalone.forEach((label) => {
			labels.push({ label })
		})

		for (const group of groupedLabels.groups.values()) {
			group.labels.forEach((label) => {
				labels.push({ label, groupName: group.groupName })
			})
		}

		return labels
	}, [groupedLabels])

	const isSearching = searchQuery.trim().length > 0
	const [openGroupId, setOpenGroupId] = useState<string | null>(null)

	const handleGroupOpenChange = (groupId: string, open: boolean) => {
		if (open) {
			setOpenGroupId(groupId)
		} else {
			if (openGroupId === groupId) {
				setOpenGroupId(null)
			}
		}
	}

	return (
		<Command shouldFilter={false}>
			<CommandInput
				value={searchQuery}
				onValueChange={onSearchChange}
				placeholder="Search labels..."
				showIcon={false}
			/>
			<CommandList>
				<CommandEmpty>
					{isSearching ? "No labels found" : "No labels available"}
				</CommandEmpty>
				{!isSearching ? (
					<CommandGroup>
						{groupedLabels.standalone.map((standaloneLabel) => {
							const isSelected = selectedLabelIds.includes(standaloneLabel.id)
							return (
								<CommandItem
									key={standaloneLabel.id}
									value={`${standaloneLabel.name}-${standaloneLabel.id}`}
									onSelect={() => {
										onChange(standaloneLabel.id)
									}}
								>
									<LabelBadge label={standaloneLabel} isSelected={isSelected} />
								</CommandItem>
							)
						})}

						{Array.from(groupedLabels.groups.entries()).map(
							([groupId, group]) => (
								<CommandSubGroup
									key={groupId}
									groupId={groupId}
									groupName={group.groupName}
									groupColor={group.groupColor}
									labels={group.labels}
									selectedLabelIds={selectedLabelIds}
									onChange={onChange}
									isOpen={openGroupId === groupId}
									onOpenChange={(open) => handleGroupOpenChange(groupId, open)}
								/>
							),
						)}
					</CommandGroup>
				) : (
					<CommandGroup>
						{allLabelsForSearch
							.filter(({ label }) =>
								label.name.toLowerCase().includes(searchQuery.toLowerCase()),
							)
							.map(({ label }) => {
								const isSelected = selectedLabelIds.includes(label.id)
								return (
									<CommandItem
										key={label.id}
										value={`${label.name}-${label.id}`}
										onSelect={() => {
											onChange(label.id)
										}}
									>
										<LabelBadge label={label} isSelected={isSelected} />
									</CommandItem>
								)
							})}
					</CommandGroup>
				)}
			</CommandList>
		</Command>
	)
}

export const LabelSelect: FC<Props> = ({
	labels,
	selectedLabelIds,
	onChange,
	variant,
	teamId,
	onOpenChange: onOpenChangeProp,
	open: openProp,
	tooltip,
	asProperty = false,
	emptyLabel,
	emptyIcon,
	trigger,
}) => {
	const { role } = useRole()
	const [internalOpen, setInternalOpen] = useToggle(false)
	const open = openProp ?? internalOpen
	const [searchQuery, setSearchQuery] = useState("")

	const handleOpenChange = (newOpen: boolean) => {
		setInternalOpen(newOpen)
		onOpenChangeProp?.(newOpen)
		if (!newOpen) {
			setSearchQuery("")
		}
	}

	const filteredLabels = useMemo(
		() => labelsForTeam(labels, teamId),
		[labels, teamId],
	)

	const groupedLabels = useMemo<GroupedLabels>(() => {
		const standalone: Label[] = []
		const groups = new Map<
			string,
			{
				groupName: string
				groupColor: string
				labels: Label[]
			}
		>()

		for (const label of filteredLabels) {
			if (!label.parentId) {
				standalone.push(label)
			} else {
				const parentLabel = labels.find((l) => l.id === label.parentId)
				if (parentLabel) {
					if (!groups.has(label.parentId)) {
						groups.set(label.parentId, {
							groupName: parentLabel.name,
							groupColor: parentLabel.color,
							labels: [],
						})
					}
					groups.get(label.parentId)?.labels.push(label)
				} else {
					standalone.push(label)
				}
			}
		}

		return { standalone, groups }
	}, [filteredLabels, labels])

	const selectedLabels = useMemo(
		() => filteredLabels.filter((label) => selectedLabelIds.includes(label.id)),
		[filteredLabels, selectedLabelIds],
	)

	return (
		<div className="flex flex-wrap gap-4">
			<Popover
				open={open}
				onOpenChange={handleOpenChange}
				className="p-0"
				// Several labels already carry a tooltip listing their names.
				triggerTooltip={selectedLabels.length > 1 ? undefined : tooltip}
				content={
					<LabelMenuContent
						groupedLabels={groupedLabels}
						selectedLabelIds={selectedLabelIds}
						onChange={(labelId) => {
							onChange(labelId)
						}}
						searchQuery={searchQuery}
						onSearchChange={(query) => {
							setSearchQuery(query)
						}}
					/>
				}
			>
				{trigger ? (
					trigger
				) : asProperty ? (
					<PropertyTrigger
						tooltip={
							selectedLabels.length > 1
								? selectedLabels.map((l) => l.name).join(", ")
								: undefined
						}
						className={cn(role === "VIEWER" && "pointer-events-none")}
						icon={
							selectedLabels.length ? (
								<div className="flex items-center">
									{selectedLabels.slice(0, 3).map((label, index) => (
										<div
											key={label.id}
											className="h-2.5 w-2.5 flex-none rounded-full ring-2 ring-white dark:ring-gray-900"
											style={{
												backgroundColor: new TinyColor(label.color).toString(),
												marginLeft: index === 0 ? 0 : -4,
												zIndex: 3 + index,
											}}
										/>
									))}
								</div>
							) : (
								<Tag className={propertyIconClassName} />
							)
						}
					>
						{selectedLabels.length === 1
							? selectedLabels[0]!.name
							: selectedLabels.length
								? `${selectedLabels.length} labels`
								: "Labels"}
					</PropertyTrigger>
				) : selectedLabels.length ? (
					<div>
						{selectedLabels.length === 1 ? (
							<Button
								variant={variant}
								disabled={role === "VIEWER"}
								className="flex items-center gap-2"
							>
								<div
									className="mr-2 h-[9px] w-[9px] flex-none rounded-full"
									style={{
										backgroundColor: new TinyColor(selectedLabels[0]!.color)
											.setAlpha(0.8)
											.toString(),
									}}
								/>

								<span className="truncate">{selectedLabels[0]!.name}</span>
							</Button>
						) : (
							<Tooltip label={selectedLabels.map((l) => l.name).join(", ")}>
								<Button
									variant={variant}
									disabled={role === "VIEWER"}
									className="flex items-center gap-2"
								>
									<div className="flex items-center">
										{selectedLabels.slice(0, 3).map((label, index) => (
											<div
												key={label.id}
												className="h-3 w-3 flex-none rounded-full border border-white dark:border-gray-900"
												style={{
													backgroundColor: new TinyColor(
														label.color,
													).toString(),
													marginLeft: index === 0 ? 0 : -5,
													zIndex: 3 + index,
												}}
											/>
										))}
									</div>

									<span className="truncate">
										{selectedLabels.length} label
										{selectedLabels.length === 1 ? "" : "s"}
									</span>
								</Button>
							</Tooltip>
						)}
					</div>
				) : emptyLabel ? (
					<Button
						variant={variant}
						className="text-tertiary dark:bg-gray-800 dark:text-secondary"
						Icon={emptyIcon ?? Tag}
						disabled={role === "VIEWER"}
					>
						{emptyLabel}
					</Button>
				) : (
					<IconButton
						className="dark:bg-gray-800"
						Icon={Tag}
						disabled={role === "VIEWER"}
					/>
				)}
			</Popover>
		</div>
	)
}
